import axios, { AxiosError } from 'axios'
import medicareApiAxios from '@cms-max/medicare-api-sdk/node_modules/axios/index'
import {
  AuthenticationApiFactory,
  ClaimDeductiblesResponse,
  ClaimDetailsRequest,
  ClaimDetailsResponse,
  ClaimsSearchRequest,
  ClaimsSearchResponse,
  IApiMessage,
  OrderMsnRequest,
  OrderMsnResponse,
  GetPreferenceResponse,
  MSPRequest,
  MSPResponse,
  PreventiveServicesResponse,
  BeneficiaryGetResponse,
  BeneficiaryModelOptions,
  CoveragePlanGetResponse,
  BeneficiaryApiFactory,
  GetMessagesBody,
  TemplateType,
  LanguageCode
} from '@cms-max/medicare-api-sdk'
import { IMedicareComplaintReducer } from 'redux/reducers/medicare-complaint-form'
import { formatToDateString, IDateObject } from 'utils/date'
import { produce } from 'immer'
import {
  EMSNSearchHttpResponse,
  EMSNSearchForm,
  GetEMSNsHttpResponse
} from 'models/eMSN'
import { Logger } from 'utils/logger'
import { getAppBaseUrl } from 'utils/getAppBaseUrl'
import {
  cloneAuthRepReducerState,
  IAuthRepsReducer
} from 'redux/reducers/auth-reps'
import { toDate, parseISO } from 'date-fns'
import {
  axiosInstance,
  axiosInstanceForBeneficiary
} from '../utils/axios-helper'
import i18nInstance from 'i18n/config'
import { CancelPromiseError } from 'utils/cancel-promise-error'
import { UpdateBeneficiaryEmailPayload } from 'models/network/UpdateBeneficiaryEmailPayload'
import { BeneficiaryFullResponse, BeneficiaryUpdatePayload } from 'models/User'
import { trimTrailingSlash } from 'utils/urls/trim-trailing-slash'

export enum MedicareApiHeaderNames {
  SlsAttemptsRemaining = 'x-sls-attempts-remaining',
  MaxSession = 'x-max-session'
}

type ApiAction<T> = () => Promise<T>

const coreApiUrl = new URL(
  process.env.REACT_APP_CORE_API_URL ?? getAppBaseUrl()
)

const endpoints = {
  beneficiaries: new URL('beneficiaries', coreApiUrl),
  beneficiaryWithReqToken: (reqToken: string) =>
    new URL(`beneficiary/${reqToken}`, coreApiUrl),
  beneficiary: new URL('beneficiary', coreApiUrl),
  updateBeneficiaryEmail: new URL('beneficiary/email', coreApiUrl),
  beneficiaryMedicareCard: new URL('beneficiary/medicare-card', coreApiUrl),
  complaintVerify: new URL('complaint/verify', coreApiUrl),
  complaintMedicare: new URL('complaint/medicare', coreApiUrl),
  authorization: new URL('authorization', coreApiUrl),
  emsnSearch: new URL('emsn/search', coreApiUrl),
  emsn: new URL('emsn', coreApiUrl),
  emsnPreference: new URL('emsn/preference', coreApiUrl),
  claims: new URL('claims', coreApiUrl),
  claimsDetails: new URL('claims/details', coreApiUrl),
  claimsDeductibles: new URL('claims/deductibles', coreApiUrl),
  messages: new URL('beneficiaries/messages', coreApiUrl),
  messageRead: new URL('beneficiaries/messages/markRead', coreApiUrl),
  msp: new URL('claims/msp', coreApiUrl),
  orderMsn: new URL('claims/order-msn', coreApiUrl),
  oifLogout: new URL('oif-logout', coreApiUrl),
  preventiveServices: new URL('preventive-services', coreApiUrl),
  plans: new URL('plans', coreApiUrl)
}

const makeApiRequest = async <T>(action: ApiAction<T>) => {
  try {
    return await action()
  } catch (error) {
    // Handle error locally
    Logger.error(error)
    let errorMessage = 'Error getting data from Core API'

    const axiosError = error as AxiosError
    if (axiosError?.response?.status) {
      errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
    }

    // Pass error back up to caller in case it wants to do special handling
    if (!(error instanceof CancelPromiseError)) {
      throw new Error(errorMessage)
    }
  }
}

const authenticationAxiosClient = medicareApiAxios.create({
  withCredentials: true
})
const beneficiaryAxiosClient = medicareApiAxios.create({
  withCredentials: true
})
const trimmedCoreApiUrl = trimTrailingSlash(coreApiUrl.toString())

/**
 * A utility function that will return false if the naviator send beacon is not a function.
 * This is so that we can easily fallback to other method for sending the payload.
 *
 * If the function exists, this will also return the response value from the navigator.sendBeacon call
 *
 * @param url url to send the request to
 * @param payload the object to pass to the url as json
 * @returns true if the navigator.sendBeacon function exists and is a function and if the request is queued successfully
 */
const sendBeaconHelper = (url: string, payload?: any) => {
  if (typeof navigator?.sendBeacon !== 'function') {
    return false
  }

  if (payload) {
    const blob = new Blob([JSON.stringify(payload)], {
      type: 'application/json'
    })
    return navigator.sendBeacon(url, blob)
  }
  return navigator.sendBeacon(url)
}

export interface IVerifyResult {
  authenticated: boolean
}

export const medicareApiClient = {
  authenticationApi: AuthenticationApiFactory(
    undefined,
    trimmedCoreApiUrl,
    authenticationAxiosClient
  ),
  beneficiaryApi: BeneficiaryApiFactory(
    undefined,
    trimmedCoreApiUrl,
    beneficiaryAxiosClient
  ),
  // Container for beacon calls since we likely won't be able to pull these in easily from the medicare api sdk
  beacons: {
    oifLogout: () => {
      sendBeaconHelper(endpoints.oifLogout.toString())
    }
  },
  async asyncVerify () {
    try {
      const response = await axios.get<IVerifyResult>(`${process.env.REACT_APP_CORE_API_URL}/verify`, { withCredentials: true })
      return response.data
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error getting data from Core API'

      if (axios.isAxiosError(error)) {
        errorMessage = `HTTP status (${error.response?.status}) - ${errorMessage}`
      }
      throw new Error(errorMessage)
    }
  },
  async asyncGetBeneficiaryByMbi (mbi: string) {
    return await makeApiRequest(async () => {
      const response = await axiosInstance.post(
        endpoints.beneficiaries.toString(),
        { mbi },
        { withCredentials: true }
      )
      return response.data
    })
  },
  /**
   * This calls gets the max session cookie for making future requests with core
   */
  async asyncGetBeneficiaryByReqToken (reqToken: string) {
    return await makeApiRequest(async () => {
      const response = await axiosInstanceForBeneficiary.get(
        endpoints.beneficiaryWithReqToken(reqToken).toString(),
        { withCredentials: true }
      )
      return response.data
    })
  },
  async asyncGetPasswordAttemptsRemaining () {
    return await makeApiRequest(async () => {
      const beneficiaryUrl = new URL(endpoints.beneficiary.toString())
      const response =
        await axiosInstanceForBeneficiary.get<BeneficiaryFullResponse>(
          beneficiaryUrl.toString(),
          { withCredentials: true }
        )
      return response.headers[MedicareApiHeaderNames.SlsAttemptsRemaining]
    })
  },
  async asyncGetBeneficiaryFullData (fields: BeneficiaryModelOptions[] = []) {
    return await makeApiRequest(async () => {
      const beneficiaryUrl = new URL(endpoints.beneficiary.toString())
      fields.forEach((field) => {
        beneficiaryUrl.searchParams.append('fields', field)
      })
      const response =
        await axiosInstanceForBeneficiary.get<BeneficiaryGetResponse>(
          beneficiaryUrl.toString(),
          { withCredentials: true }
        )
      return {
        user: response.data,
        sessionId: response.headers[MedicareApiHeaderNames.MaxSession]
      }
    })
  },
  async asyncGetBeneficiaryFullDataWithAccountSettings () {
    return this.asyncGetBeneficiaryFullData([
      BeneficiaryModelOptions.Email,
      BeneficiaryModelOptions.EHandbookPreferences,
      BeneficiaryModelOptions.EMsnPreferences,
      BeneficiaryModelOptions.LastCardRequestDateMs
    ])
  },
  async asyncUpdateBeneficiary (payload: BeneficiaryUpdatePayload) {
    return await makeApiRequest(async () => {
      const response = await axiosInstance.patch(
        endpoints.beneficiary.toString(),
        payload,
        { withCredentials: true }
      )
      return {
        user: response.data,
        sessionId: response.headers[MedicareApiHeaderNames.MaxSession]
      }
    })
  },
  async asyncUpdateBeneficiaryEmail (payload: UpdateBeneficiaryEmailPayload) {
    return await makeApiRequest(async () => {
      const beneficiaryEmailUpdateUrl = endpoints.updateBeneficiaryEmail
      const response = await axiosInstance.put(
        beneficiaryEmailUpdateUrl.toString(),
        payload,
        { withCredentials: true }
      )
      return response.data
    })
  },
  async asyncPostBeneficiaryMedicareCard () {
    return await makeApiRequest(async () => {
      const beneficiaryMedicareCard = endpoints.beneficiaryMedicareCard
      const response = await axiosInstance.post(
        beneficiaryMedicareCard.toString(),
        { withCredentials: true }
      )
      return response
    })
  },
  async asyncGetBeneficiaryMedicareCard () {
    const beneficiaryMedicareCard = endpoints.beneficiaryMedicareCard
    try {
      const response = await axiosInstance.get(
        beneficiaryMedicareCard.toString(),
        { withCredentials: true }
      )
      return response.data
    } catch (e: unknown) {
      if (axios.isAxiosError(e)) {
        if (e.response?.status === 403) {
          throw new Error('Ineligible Beneficiary')
        }
      }
      throw new Error('Failed to retrieve medicare card information')
    }
  },
  async asyncSubmitStep1 (payload: IMedicareComplaintReducer) {
    return await makeApiRequest(async () => {
      const newPayload = produce(payload, (draftState) => {
        const coverageStartDateString = formatToDateString(
          payload.coverageStartDate as IDateObject
        )
        const dateOfBirthString = formatToDateString(
          payload.dateOfBirth as IDateObject
        )
        draftState.coverageStartDate = coverageStartDateString as any
        draftState.dateOfBirth = dateOfBirthString as any
      })

      const response = await axiosInstance.post(
        endpoints.complaintVerify.toString(),
        newPayload
      )
      return response.data
    })
  },
  async asyncSubmitStep2 (payload: IMedicareComplaintReducer) {
    return await makeApiRequest(async () => {
      const newPayload = produce(payload, (draftState) => {
        const coverageStartDateString = formatToDateString(
          payload.coverageStartDate as IDateObject
        )
        const dateOfBirthString = formatToDateString(
          payload.dateOfBirth as IDateObject
        )
        draftState.coverageStartDate = coverageStartDateString as any
        draftState.dateOfBirth = dateOfBirthString as any
        draftState.type = 'VERIFIED'
      })

      const response = await axiosInstance.post(
        endpoints.complaintMedicare.toString(),
        newPayload
      )
      return response.data
    })
  },
  async asyncSubmitStep2b (payload: IMedicareComplaintReducer) {
    return await makeApiRequest(async () => {
      const newPayload = produce(payload, (draftState) => {
        draftState.type = 'UNVERIFIED'
      })

      const response = await axiosInstance.post(
        endpoints.complaintMedicare.toString(),
        newPayload
      )
      return response.data
    })
  },
  async asyncGetRepsByReqToken () {
    return await makeApiRequest(async () => {
      const response = await axiosInstance.get(
        endpoints.authorization.toString(),
        { withCredentials: true }
      )
      response.data.forEach((rep: any) => {
        const isNotIndefinite = rep.disclosurePeriod !== 'disclose_indefinitely'
        const endDate = toDate(parseISO(rep.authEndDate))
        rep.authEndDate = {
          month: isNotIndefinite ? endDate.getMonth() + 1 : undefined,
          day: isNotIndefinite ? endDate.getDate() : undefined,
          year: isNotIndefinite ? endDate.getFullYear() : undefined
        }
        const startDate = toDate(parseISO(rep.authStartDate))
        // Start month could be 'zero', so we can't do a truthy/falsy check on it
        const startMonthNullish =
          startDate.getMonth() === undefined || startDate.getMonth() === null
        // we still want start date when the rep is indefinite
        rep.authStartDate = {
          month: !startMonthNullish ? startDate.getMonth() + 1 : undefined,
          day: startDate.getDate() ?? undefined,
          year: startDate.getFullYear() ?? undefined
        }
        return rep
      })
      return response.data
    })
  },
  async getEMSNs () {
    return await makeApiRequest<GetEMSNsHttpResponse>(async () => {
      const response = await axiosInstance.get(endpoints.emsn.toString(), {
        withCredentials: true
      })
      return response.data
    })
  },
  async asyncGetEMSNPreference () {
    return await makeApiRequest<GetPreferenceResponse>(async () => {
      const response = await axiosInstance.get(
        endpoints.emsnPreference.toString(),
        { withCredentials: true }
      )
      return response.data
    })
  },
  async searchEMSNs (searchCriteria: EMSNSearchForm) {
    return await makeApiRequest<EMSNSearchHttpResponse>(async () => {
      const response = await axiosInstance.post(
        endpoints.emsnSearch.toString(),
        {
          claimNumber: searchCriteria.claimNumber
        },
        { withCredentials: true }
      )
      return response.data
    })
  },
  async asyncSearchClaims (request: ClaimsSearchRequest) {
    return await makeApiRequest<ClaimsSearchResponse>(async () => {
      const response = await axiosInstance.post(
        endpoints.claims.toString(),
        request,
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncGetClaimsDetails (request: ClaimDetailsRequest) {
    return await makeApiRequest<ClaimDetailsResponse>(async () => {
      const response = await axiosInstance.post(
        endpoints.claimsDetails.toString(),
        request,
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncGetClaimsDeductibles () {
    return makeApiRequest<ClaimDeductiblesResponse>(async () => {
      const response = await axiosInstance.get(
        endpoints.claimsDeductibles.toString(),
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncFetchMspSummaries ({ classType }: MSPRequest) {
    return await makeApiRequest<MSPResponse>(async () => {
      const response = await axiosInstance.post(
        endpoints.msp.toString(),
        { classType },
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncOrderMsn (request: OrderMsnRequest) {
    return await makeApiRequest<OrderMsnResponse>(async () => {
      const response = await axiosInstance.post(
        endpoints.orderMsn.toString(),
        request,
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncAddRep (payload: IAuthRepsReducer) {
    const payloadResult = removeOptionalAuthRepFields(payload)
    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_CORE_API_URL!}/authorization`,
        payloadResult,
        { withCredentials: true }
      )
      return response.data
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error getting data from Core API'

      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      if (!(error instanceof CancelPromiseError)) {
        throw new Error(errorMessage)
      }
    }
  },
  async asyncUpdateRep (payload: IAuthRepsReducer) {
    const payloadResult = removeOptionalAuthRepFields(payload)
    try {
      if (!payload.id) {
        throw new Error('id required')
      }
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_CORE_API_URL!}/authorization`,
        payloadResult,
        { withCredentials: true }
      )
      return response.data
    } catch (error) {
      Logger.error(error)
      let errorMessage = 'Error getting data from Core API'
      const axiosError = error as AxiosError
      if (axiosError?.response?.status) {
        errorMessage = `HTTP status (${axiosError.response.status}) - ${errorMessage}`
      }

      if (!(error instanceof CancelPromiseError)) {
        throw new Error(errorMessage)
      }
    }
  },
  async asyncGetPreventiveServices () {
    return makeApiRequest<PreventiveServicesResponse>(async () => {
      const response = await axiosInstance.get(
        endpoints.preventiveServices.toString(),
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncGetPlans () {
    return makeApiRequest<CoveragePlanGetResponse>(async () => {
      const response = await axiosInstance.get(endpoints.plans.toString(), {
        headers: {
          'Accept-Language': i18nInstance.language
        },
        withCredentials: true
      })
      return response.data
    })
  },
  async asyncGetMessages ({ templateType }: { templateType: TemplateType }) {
    return makeApiRequest<IApiMessage[]>(async () => {
      const payload: GetMessagesBody = {
        languageCode: i18nInstance.language as LanguageCode,
        templateType
      }
      const response = await axiosInstance.post(
        endpoints.messages.toString(),
        payload,
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  },
  async asyncMarkMessageRead (payload: { messageIds: string[] }) {
    return makeApiRequest(async () => {
      const response = await axiosInstance.post(
        endpoints.messageRead.toString(),
        payload,
        {
          headers: {
            'Accept-Language': i18nInstance.language
          },
          withCredentials: true
        }
      )
      return response.data
    })
  }
}

export const removeOptionalAuthRepFields = (reducerState: IAuthRepsReducer) => {
  const payload = cloneAuthRepReducerState(reducerState)
  if (payload.disclosurePeriod === 'disclose_indefinitely') {
    delete payload.authStartDate
    delete payload.authEndDate
  } else {
    const authStartDateString = formatToDateString(
      payload.authStartDate as IDateObject
    )
    const authEndDateString = formatToDateString(
      payload.authEndDate as IDateObject
    )
    payload.authStartDate = authStartDateString as any
    payload.authEndDate = authEndDateString as any
  }
  if (!payload.disclosureLimitations.isOtherSpecificInfoChecked) {
    delete payload.disclosureLimitations?.otherSpecificInformation
  }
  if (payload.disclosureLimitations.limitedInformation === 'any_information') {
    delete payload.disclosureLimitations?.isInfoMedicareAllowedChecked
    delete payload.disclosureLimitations?.isInfoMedicareClaimsChecked
    delete payload.disclosureLimitations?.isInfoPlanEnrollmentChecked
    delete payload.disclosureLimitations?.isInfoPremiumPaymentsChecked
    delete payload.disclosureLimitations?.isOtherSpecificInfoChecked
    delete payload.disclosureLimitations?.otherSpecificInformation
  }
  return payload
}
